import { useState, useEffect } from 'react';
import { pb, tryRefreshToken } from './pocketbase';
import { AuthContext } from './authContext';
import { AuthModel, AuthContextType } from './authTypes';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthModel>(pb.authStore.model);
  const [loading, setLoading] = useState(true);

  // Update auth state when PocketBase auth store changes
  useEffect(() => {
    // Try to refresh the token on app initialization
    async function refreshToken() {
      try {
        // Attempt to refresh the token if one exists
        await tryRefreshToken();
        setUser(pb.authStore.model);
      } catch {
        // If refresh fails, clear the auth store
        pb.authStore.clear();
        setUser(null);
      } finally {
        setLoading(false);
      }
    }

    refreshToken();

    // Subscribe to auth state changes
    pb.authStore.onChange((_token, model) => {
      setUser(model);
    });

    // Check token validity
    if (pb.authStore.model && !pb.authStore.isValid) {
      // Token has expired, so log out the user
      pb.authStore.clear();
      setUser(null);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const authData = await pb.collection('users').authWithPassword(email, password);
      setUser(authData.record);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string, passwordConfirm: string) => {
    try {
      const data = {
        email,
        password,
        passwordConfirm,
      };
      const createdUser = await pb.collection('users').create(data);
      // Automatically log in after registration
      await login(email, password);
      return createdUser;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = () => {
    pb.authStore.clear();
    setUser(null);
  };

  // Helper to check auth state and handle expired tokens
  const checkAuthAndLogout = () => {
    if (pb.authStore.model && !pb.authStore.isValid) {
      logout();
      return false;
    }
    return !!pb.authStore.model;
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    register,
    loading,
    checkAuthAndLogout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
} 